// API-based Test Runner
// Tests actual backend services and data flow instead of DOM elements

import { auth, db } from '../firebase/config';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { collection, addDoc, getDocs, limit, query } from 'firebase/firestore';

export interface APITestResult {
  testName: string;
  status: 'pass' | 'fail' | 'skip';
  message: string;
  duration: number;
  data?: any;
  error?: string;
}

export interface APITestSuite {
  name: string;
  tests: APITestResult[];
  totalTests: number;
  passedTests: number;
  failedTests: number;
  skippedTests: number;
  duration: number;
}

class APITestRunner {
  private results: APITestSuite[] = [];
  private currentSuite: APITestSuite | null = null;

  async runAllTests(): Promise<APITestSuite[]> {
    console.log('🧪 Starting API test suite...');
    
    // Clear previous results
    this.results = [];
    
    // Run all test suites
    await this.testFirebaseConnection();
    await this.testAuthentication();
    await this.testFirestoreOperations();
    await this.testAnalyticsServices();
    await this.testContentServices();
    await this.testUserServices();
    await this.testEmailServices();
    await this.testAutomationServices();
    await this.testReaderServices();
    await this.testPerformance();
    
    console.log('✅ API test suite complete');
    return this.results;
  }

  private async startSuite(name: string): Promise<void> {
    this.currentSuite = {
      name,
      tests: [],
      totalTests: 0,
      passedTests: 0,
      failedTests: 0,
      skippedTests: 0,
      duration: 0
    };
    this.results.push(this.currentSuite);
  }

  private async endSuite(): Promise<void> {
    if (!this.currentSuite) return;
    
    const endTime = Date.now();
    this.currentSuite.duration = endTime - Date.now();
    this.currentSuite.totalTests = this.currentSuite.tests.length;
    this.currentSuite.passedTests = this.currentSuite.tests.filter(t => t.status === 'pass').length;
    this.currentSuite.failedTests = this.currentSuite.tests.filter(t => t.status === 'fail').length;
    this.currentSuite.skippedTests = this.currentSuite.tests.filter(t => t.status === 'skip').length;
  }

  private async addTest(name: string, testFn: () => Promise<boolean>, message: string = '', data?: any): Promise<void> {
    if (!this.currentSuite) return;

    const startTime = Date.now();
    try {
      const result = await testFn();
      const duration = Date.now() - startTime;
      
      this.currentSuite.tests.push({
        testName: name,
        status: result ? 'pass' : 'fail',
        message: result ? message || 'Test passed' : 'Test failed',
        duration,
        data
      });
    } catch (error) {
      const duration = Date.now() - startTime;
      this.currentSuite.tests.push({
        testName: name,
        status: 'fail',
        message: `Error: ${error}`,
        duration,
        error: String(error)
      });
    }
  }

  // Firebase Connection Tests
  private async testFirebaseConnection(): Promise<void> {
    await this.startSuite('Firebase Connection');
    
    await this.addTest(
      'Firebase Auth initialized',
      async () => {
        return !!auth;
      },
      'Firebase Auth is properly initialized'
    );

    await this.addTest(
      'Firestore initialized',
      async () => {
        return !!db;
      },
      'Firestore is properly initialized'
    );

    await this.addTest(
      'Firebase config loaded',
      async () => {
        return auth.app.options.projectId === 'fulcrum-41e50';
      },
      'Firebase project configuration is correct'
    );

    await this.endSuite();
  }

  // Authentication Tests
  private async testAuthentication(): Promise<void> {
    await this.startSuite('Authentication Services');
    
    await this.addTest(
      'Auth service available',
      async () => {
        return !!auth && typeof signInWithEmailAndPassword === 'function';
      },
      'Authentication service methods are available'
    );

    await this.addTest(
      'Auth state persistence',
      async () => {
        return auth.currentUser === null || typeof auth.currentUser === 'object';
      },
      'Auth state persistence is working'
    );

    await this.addTest(
      'Auth error handling',
      async () => {
        try {
          // Try invalid login - should not throw unhandled error
          await signInWithEmailAndPassword(auth, 'invalid@test.com', 'wrongpassword');
          return false;
        } catch (error: any) {
          return error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password';
        }
      },
      'Authentication error handling works correctly'
    );

    await this.endSuite();
  }

  // Firestore Operations Tests
  private async testFirestoreOperations(): Promise<void> {
    await this.startSuite('Firestore Operations');
    
    await this.addTest(
      'Firestore collections accessible',
      async () => {
        try {
          const testRef = collection(db, '_test');
          return !!testRef;
        } catch (error) {
          return false;
        }
      },
      'Can access Firestore collections'
    );

    await this.addTest(
      'Firestore write operations',
      async () => {
        try {
          const testData = { test: true, timestamp: new Date() };
          const docRef = await addDoc(collection(db, '_test'), testData);
          return !!docRef.id;
        } catch (error) {
          return false;
        }
      },
      'Can write to Firestore'
    );

    await this.addTest(
      'Firestore read operations',
      async () => {
        try {
          const q = query(collection(db, '_test'), limit(1));
          const snapshot = await getDocs(q);
          return !snapshot.empty;
        } catch (error) {
          return false;
        }
      },
      'Can read from Firestore'
    );

    await this.endSuite();
  }

  // Analytics Services Tests
  private async testAnalyticsServices(): Promise<void> {
    await this.startSuite('Analytics Services');
    
    await this.addTest(
      'Analytics collection accessible',
      async () => {
        try {
          const analyticsRef = collection(db, 'analytics');
          return !!analyticsRef;
        } catch (error) {
          return false;
        }
      },
      'Analytics collection is accessible'
    );

    await this.addTest(
      'Analytics data write test',
      async () => {
        try {
          const testData = {
            event: 'test_event',
            timestamp: new Date(),
            userId: 'test-user'
          };
          await addDoc(collection(db, 'analytics'), testData);
          return true;
        } catch (error) {
          return false;
        }
      },
      'Can write analytics data'
    );

    await this.addTest(
      'Analytics data read test',
      async () => {
        try {
          const q = query(collection(db, 'analytics'), limit(1));
          const snapshot = await getDocs(q);
          return !snapshot.empty;
        } catch (error) {
          return false;
        }
      },
      'Can read analytics data'
    );

    await this.endSuite();
  }

  // Content Services Tests
  private async testContentServices(): Promise<void> {
    await this.startSuite('Content Services');
    
    await this.addTest(
      'Posts collection accessible',
      async () => {
        try {
          const postsRef = collection(db, 'posts');
          return !!postsRef;
        } catch (error) {
          return false;
        }
      },
      'Posts collection is accessible'
    );

    await this.addTest(
      'Content creation test',
      async () => {
        try {
          const testPost = {
            title: 'API Test Post',
            content: 'This is a test post for API testing',
            authorId: 'test-user',
            status: 'draft',
            createdAt: new Date()
          };
          
          const docRef = await addDoc(collection(db, 'posts'), testPost);
          return !!docRef.id;
        } catch (error) {
          return false;
        }
      },
      'Content creation works'
    );

    await this.addTest(
      'Content retrieval test',
      async () => {
        try {
          const q = query(collection(db, 'posts'), limit(5));
          const snapshot = await getDocs(q);
          return !snapshot.empty;
        } catch (error) {
          return false;
        }
      },
      'Content retrieval works'
    );

    await this.endSuite();
  }

  // User Services Tests
  private async testUserServices(): Promise<void> {
    await this.startSuite('User Services');
    
    await this.addTest(
      'Users collection accessible',
      async () => {
        try {
          const usersRef = collection(db, 'users');
          return !!usersRef;
        } catch (error) {
          return false;
        }
      },
      'Users collection is accessible'
    );

    await this.addTest(
      'User data write test',
      async () => {
        try {
          const testUser = {
            email: 'test@example.com',
            displayName: 'Test User',
            createdAt: new Date()
          };
          
          const docRef = await addDoc(collection(db, 'users'), testUser);
          return !!docRef.id;
        } catch (error) {
          return false;
        }
      },
      'User data write works'
    );

    await this.endSuite();
  }

  // Email Services Tests
  private async testEmailServices(): Promise<void> {
    await this.startSuite('Email Services');
    
    await this.addTest(
      'Email templates collection accessible',
      async () => {
        try {
          const templatesRef = collection(db, 'emailTemplates');
          return !!templatesRef;
        } catch (error) {
          return false;
        }
      },
      'Email templates collection is accessible'
    );

    await this.addTest(
      'Email campaigns collection accessible',
      async () => {
        try {
          const campaignsRef = collection(db, 'emailCampaigns');
          return !!campaignsRef;
        } catch (error) {
          return false;
        }
      },
      'Email campaigns collection is accessible'
    );

    await this.endSuite();
  }

  // Automation Services Tests
  private async testAutomationServices(): Promise<void> {
    await this.startSuite('Automation Services');
    
    await this.addTest(
      'Automation campaigns collection accessible',
      async () => {
        try {
          const campaignsRef = collection(db, 'automationCampaigns');
          return !!campaignsRef;
        } catch (error) {
          return false;
        }
      },
      'Automation campaigns collection is accessible'
    );

    await this.addTest(
      'Templates collection accessible',
      async () => {
        try {
          const templatesRef = collection(db, 'templates');
          return !!templatesRef;
        } catch (error) {
          return false;
        }
      },
      'Templates collection is accessible'
    );

    await this.endSuite();
  }

  // Reader Services Tests
  private async testReaderServices(): Promise<void> {
    await this.startSuite('Reader Services');
    
    await this.addTest(
      'Reading queue collection accessible',
      async () => {
        try {
          const queueRef = collection(db, 'readingQueue');
          return !!queueRef;
        } catch (error) {
          return false;
        }
      },
      'Reading queue collection is accessible'
    );

    await this.addTest(
      'Reader preferences collection accessible',
      async () => {
        try {
          const preferencesRef = collection(db, 'readerPreferences');
          return !!preferencesRef;
        } catch (error) {
          return false;
        }
      },
      'Reader preferences collection is accessible'
    );

    await this.endSuite();
  }

  // Performance Tests
  private async testPerformance(): Promise<void> {
    await this.startSuite('Performance Tests');
    
    await this.addTest(
      'API response time',
      async () => {
        const startTime = Date.now();
        try {
          const q = query(collection(db, '_test'), limit(1));
          await getDocs(q);
          const duration = Date.now() - startTime;
          return duration < 5000; // Should respond within 5 seconds
        } catch (error) {
          return false;
        }
      },
      'API responses are fast enough'
    );

    await this.addTest(
      'Memory usage reasonable',
      async () => {
        const memory = (performance as any).memory;
        if (memory) {
          return memory.usedJSHeapSize < 100 * 1024 * 1024; // 100MB
        }
        return true; // Skip if memory API not available
      },
      'Memory usage is reasonable'
    );

    await this.endSuite();
  }

  // Get test statistics
  getStatistics(): any {
    const allTests = this.results.flatMap(suite => suite.tests);
    const totalTests = allTests.length;
    const passedTests = allTests.filter(t => t.status === 'pass').length;
    const failedTests = allTests.filter(t => t.status === 'fail').length;
    const skippedTests = allTests.filter(t => t.status === 'skip').length;
    const successRate = totalTests > 0 ? (passedTests / totalTests) * 100 : 0;

    return {
      totalTests,
      passedTests,
      failedTests,
      skippedTests,
      successRate: Math.round(successRate * 100) / 100,
      totalDuration: this.results.reduce((sum, suite) => sum + suite.duration, 0)
    };
  }
}

export const apiTestRunner = new APITestRunner();
